import { type FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAccountRecoveryV2, AccountVerificationType, AccountRecoveryErrorType } from '@egw/api'
import { Alert, AlertType, Button, Input } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { useRouter } from '@egw/router'
import { Logger } from '@egw/logger'
import { sendEvent } from '@egw/events'
import { Layout, PageHeader } from '../../../components'
import { getRecoveryFlowData, setRecoveryFlowData, updateRecoveryFlowData } from '../../../services'
import { ChannelType } from '../../../models'

const log = new Logger('Passkey Recovery - Send Email')

interface FormData {
  email: string
}

export const RecoverySendEmailPage: FC = () => {
  const { t } = useTranslation('features-auth')
  const { navigate } = useRouter()

  const { mutate, isLoading, error } = useAccountRecoveryV2()

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid },
    setValue,
  } = useForm({
    mode: 'all',
    defaultValues: {
      email: '',
    },
  })

  useEffect(() => {
    // Check recovery flow data
    const data = getRecoveryFlowData()
    if (!data) {
      setRecoveryFlowData({ channel: ChannelType.EMAIL, isPhoneVerified: false, isEmailVerified: false })
    }
  }, [])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    sendEvent('user_sendVerification_click', {
      eventSource: 'egWealth',
      category: 'passkey-recovery',
      subCategory: 'verification',
      verificationType: 'email',
    })
    const formData = fData as FormData
    updateRecoveryFlowData((data) => {
      data.email = formData.email
      data.isEmailVerified = false
      data.isPhoneVerified = false
    })
    try {
      const response = await mutate({
        email: formData.email,
        sendVerificationMethod: true,
        verifyType: AccountVerificationType.EMAIL_MAGIC_LINK,
      })
      if (response?.__typename === 'AccountRecoveryError' && response.errorType === AccountRecoveryErrorType.EXPIRED) {
        navigate(`${WEALTH_ROUTES.SIGN_IN.path}?recoveryExpired=true`)
        return
      }
      navigate(WEALTH_ROUTES.RECOVERY_MAGIC_LINK.path)
    } catch (e) {
      log.error('Error sending email recovery code', e)
    }
  })

  return (
    <Layout>
      <form onSubmit={(e) => void onSubmit(e)} className="flex-1 flex flex-col justify-start md:justify-center">
        <PageHeader title={t('recovery.title')} backLinkUrl={WEALTH_ROUTES.RECOVERY_SEND_PHONE.backPath} />

        <p className="mb-sm">{t('recovery.send_email_message')}</p>

        <Controller
          name="email"
          control={control}
          rules={{
            required: t('recovery.required_field'),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]/i,
              message: t('recovery.invalid_email'),
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              label={t('recovery.email')}
              required
              error={formErrors.email?.message}
              autoComplete="email"
              type="email"
              onChange={(e) => {
                field.onChange(e)
                if (formErrors.email) {
                  setValue('email', e.target.value, { shouldValidate: true })
                }
              }}
            />
          )}
        />

        <div className="flex-1 md:hidden" />

        {error ? (
          <Alert alertType={AlertType.Error} description={t('recovery.generic_error')} className="mt-sm" />
        ) : null}

        <Button
          type="submit"
          className="w-full md:w-min mt-lg"
          isDisabled={!isValid}
          isLoading={isLoading}
          testId="submitSendCode_primary_btn"
        >
          {t('recovery.send_link')}
        </Button>
      </form>
    </Layout>
  )
}
